import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Home, CheckCircle2, XCircle, Loader2 } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";

/* Household invitation accept page: /household/accept?token=... */

export default function HouseholdAccept() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const { user, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const [info, setInfo] = useState(null);
  const [state, setState] = useState("idle"); // idle | accepting | done | error
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!token) { setState("error"); setErr("Missing invitation link"); return; }
    api.get(`/household/invite-info?token=${encodeURIComponent(token)}`)
      .then((r) => setInfo(r.data))
      .catch(() => { setState("error"); setErr("This invitation was not found or has already been used."); });
  }, [token]);

  const accept = async () => {
    setState("accepting");
    try {
      await api.post("/household/accept", { token });
      setState("done");
    } catch (e) {
      setState("error");
      setErr(e?.response?.data?.detail || "Could not accept the invitation.");
    }
  };

  if (authLoading) return null;

  return (
    <div className="max-w-xl mx-auto px-5 py-20 text-center" data-testid="household-accept-page">
      <Home className="w-12 h-12 text-indigo-600 mx-auto mb-4" />
      {state === "error" ? (
        <>
          <XCircle className="w-10 h-10 text-rose-500 mx-auto mb-3" />
          <h1 className="font-heading font-900 text-2xl">Invitation problem</h1>
          <p className="text-muted-foreground mt-2" data-testid="accept-error">{err}</p>
        </>
      ) : state === "done" ? (
        <>
          <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto mb-3" />
          <h1 className="font-heading font-900 text-2xl" data-testid="accept-done">Welcome to the household!</h1>
          <p className="text-zinc-600 mt-2">You now share a My Travel Hub membership. Your own trips and profile stay private unless you share them.</p>
          <button onClick={() => nav("/travel-hub")} className="mt-6 bg-indigo-700 text-white font-700 px-8 py-3 hover:bg-indigo-600">Open My Travel Hub</button>
        </>
      ) : !info ? (
        <Loader2 className="w-8 h-8 text-indigo-600 animate-spin mx-auto" />
      ) : (
        <>
          <h1 className="font-heading font-900 text-3xl">{info.inviter} invited you</h1>
          <p className="text-zinc-600 mt-3">Join their My Travel Hub membership on TravelerPulse — plan trips together while keeping your own private travel profile and login.</p>
          {user ? (
            <button onClick={accept} disabled={state === "accepting"} data-testid="accept-btn"
              className="mt-6 bg-indigo-700 text-white font-heading font-900 px-10 py-4 hover:bg-indigo-600 disabled:opacity-50">
              {state === "accepting" ? "Joining…" : "Accept invitation"}
            </button>
          ) : (
            <div className="mt-6">
              <p className="text-sm text-muted-foreground mb-3">Sign in or create your free TravelerPulse account first — then come back to this link.</p>
              <Link to="/login" data-testid="accept-login" className="inline-block bg-indigo-700 text-white font-700 px-8 py-3 hover:bg-indigo-600">Sign in / Create account</Link>
            </div>
          )}
        </>
      )}
    </div>
  );
}
